import { html, LitElement, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';

export class BoilerplateDrawer extends LitElement {
  static get properties() {
    return {
      transparent: { type: Boolean },
      overlay: { type: Boolean },
      class: { type: String },
      zIndex: { type: String },
      position: { type: String },
      open: { type: Boolean },
      title: { type: String, attribute: true },
      detail: { type: Object, state: true },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.namespace = this.tagName.toLowerCase();
    this.drawerClass = this.namespace.toLowerCase().replaceAll('-', '_');
    this.position = 'bottom';
    this.overlay = true;
    this.transparent = false;
    this.zIndex = '';
    this.closeTimeout = null;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  getDrawerTransitionProperty() {
    return this.prefersReducedMotion() ? 'opacity' : 'transform';
  }

  getDrawerTransitionDuration() {
    return 300;
  }

  animateDrawerOpen() {
    this.drawerRef.classList.remove('_drawer_is_open');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawerRef?.classList.add('_drawer_is_open');
      });
    });
  }

  animateDrawerClose(finishClose) {
    this.drawerRef.classList.remove('_drawer_is_open');

    const transitionProperty = this.getDrawerTransitionProperty();

    const onTransitionEnd = (event) => {
      if (event.target !== this.drawerRef) {
        return;
      }

      if (event.propertyName !== transitionProperty) {
        return;
      }

      this.drawerRef.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
      finishClose();
    };

    this.drawerRef.addEventListener('transitionend', onTransitionEnd);
    this.closeTimeout = setTimeout(() => {
      this.drawerRef.removeEventListener('transitionend', onTransitionEnd);
      this.closeTimeout = null;
      finishClose();
    }, this.getDrawerTransitionDuration() + 50);
  }

  connectedCallback() {
    super.connectedCallback();

    this.boundToggleDrawer = this.toggleDrawer.bind(this);

    document.addEventListener(
      `${this.namespace}:open`,
      this.boundToggleDrawer
    );

    document.addEventListener(
      `${this.namespace}:close`,
      this.boundToggleDrawer
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      `${this.namespace}:open`,
      this.boundToggleDrawer
    );

    document.removeEventListener(
      `${this.namespace}:close`,
      this.boundToggleDrawer
    );
  }

  handleToggleDrawer(e) {
    const { target, currentTarget } = e;

    if (target === currentTarget) {
      this.toggleDrawer(e);
    }
  }

  handleCancel(e) {
    e.preventDefault();
    this.closeDrawer();
  }

  toggleDrawer(e) {
    if (!this.drawerRef) {
      return;
    }

    if (this.open) {
      this.closeDrawer();
      return;
    }

    this.openDrawer(e);
  }

  openDrawer(e) {
    if (!this.drawerRef || this.open) {
      return;
    }

    const detail = e?.detail;

    if (detail) {
      this.detail = detail;
    }

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.open = true;

    const body = document.querySelector('body');
    body?.classList.add('overflow-hidden');

    this.drawerRef.showModal();
    this.animateDrawerOpen();
    this.dispatchDrawerState(true);
    this.onDrawerOpen(e);
  }

  closeDrawer() {
    if (!this.drawerRef || !this.open) {
      return;
    }

    this.open = false;

    const finishClose = () => {
      if (this.drawerRef?.open) {
        this.drawerRef.close();
      }

      document.querySelector('body')?.classList.remove('overflow-hidden');
      this.dispatchDrawerState(false);
      this.onDrawerClose();
    };

    this.animateDrawerClose(finishClose);
  }

  dispatchDrawerState(is_open) {
    document.dispatchEvent(
      new CustomEvent(`${this.namespace}:state`, {
        bubbles: true,
        detail: is_open,
      })
    );
  }

  onDrawerOpen() {}

  onDrawerClose() {}

  renderHeader() {
    return html`
      <h2 id=${`${this.namespace}_title`}>${this.title}</h2>
      <button
        class="_drawer_close"
        aria-label="Close"
        @click=${this.closeDrawer}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
            fill="black"
          />
        </svg>
      </button>
    `;
  }

  renderContent() {
    return html`
      <header class="_drawer_header">
        <div class="_drawer_header_wrapper">${this.renderHeader()}</div>
        <div class="_drawer_divider"></div>
      </header>
      <div class="_drawer_body">${this.renderBody()}</div>
      ${this.renderFooter()}
    `;
  }

  renderBody() {
    return nothing;
  }

  renderFooter() {
    return nothing;
  }

  render() {
    return html`
      <dialog
        aria-labelledby=${`${this.namespace}_title`}
        aria-label=${this.title}
        aria-modal="true"
        position=${this.position}
        ?transparent=${this.transparent}
        ?inert=${!this.open}
        @cancel=${this.handleCancel}
        @click=${this.handleToggleDrawer}
        tabindex=${this.open ? '0' : '-1'}
        class="${this.class} ${this.zIndex} _drawer _drawer_position_${this.position} ${this.drawerClass}"
        ${ref((el) => (this.drawerRef = el))}
      >
        ${this.renderContent()}
      </dialog>
    `;
  }
}
